import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
  Stack,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../containers/ApiContainer';
import { ACTIVATION_STATE } from '../../../components/Activation/types';
import { ActivationProgress } from '../../../components/Activation';
import { ActivationOverviewRow, ActivationFilter } from './types';
import { ActivationDetailDialog } from './ActivationDetailDialog';

/**
 * Get chip color based on activation state
 */
function getStateChipColor(state: number): 'default' | 'warning' | 'success' {
  switch (state) {
    case ACTIVATION_STATE.DEEP:
      return 'success';
    case ACTIVATION_STATE.SHALLOW:
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * Get state label
 */
function getStateLabel(state: number, t: (key: string) => string): string {
  switch (state) {
    case ACTIVATION_STATE.DEEP:
      return t('screens.superAdmin.activation.overview.states.deep');
    case ACTIVATION_STATE.SHALLOW:
      return t('screens.superAdmin.activation.overview.states.shallow');
    default:
      return t('screens.superAdmin.activation.overview.states.pre');
  }
}

/**
 * Format relative time
 */
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
}

/**
 * Calculate timeline info (start date, expiration, days remaining)
 */
function getTimelineInfo(row: ActivationOverviewRow) {
  if (!row.activation_started_at) return null;

  const startDate = new Date(row.activation_started_at);
  const expirationDate = new Date(startDate);
  expirationDate.setDate(expirationDate.getDate() + row.activation_window_days);

  const now = new Date();
  const daysRemaining = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return {
    startDate,
    expirationDate,
    daysRemaining,
    isExpired: daysRemaining < 0,
  };
}

interface FboOption {
  id: string;
  name: string;
}

export const ActivationAdmin: React.FC = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const [filter, setFilter] = React.useState<ActivationFilter>('all');
  const [selectedFboId, setSelectedFboId] = React.useState<string>('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [fbos, setFbos] = React.useState<FboOption[]>([]);
  const [data, setData] = React.useState<ActivationOverviewRow[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedUser, setSelectedUser] = React.useState<ActivationOverviewRow | null>(null);

  // Fetch FBOs for dropdown
  React.useEffect(() => {
    if (!postgrest) return;

    postgrest
      .from('fbo')
      .select('id, name')
      .order('name')
      .then(({ data: fboData }) => {
        setFbos(fboData || []);
      });
  }, [postgrest]);

  // Fetch activation overview data via admin RPC function
  const fetchData = React.useCallback(async () => {
    if (!postgrest) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data: rows, error: fetchError } = await postgrest.rpc(
        'admin_get_activation_overview',
        {
          p_fbo_id: selectedFboId || null,
          p_search: searchQuery.trim() || null,
          p_filter: filter,
        }
      );

      if (fetchError) throw fetchError;
      setData(rows || []);
    } catch (err) {
      console.error('Failed to fetch activation data:', err);
      setError('Failed to load activation data');
    } finally {
      setIsLoading(false);
    }
  }, [postgrest, filter, selectedFboId, searchQuery]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle activation mode toggle - use upsert to create record if needed
  const handleToggleMode = React.useCallback(async (row: ActivationOverviewRow) => {
    if (!postgrest) return;

    try {
      const newMode = !row.activation_mode;

      // Use upsert - creates record if doesn't exist, updates if it does
      const { error: upsertError } = await postgrest
        .from('user_activation')
        .upsert({
          user_id: row.user_id,
          fbo_id: row.fbo_id,
          activation_mode: newMode,
          activation_started_at: newMode ? new Date().toISOString() : null,
        }, { onConflict: 'user_id,fbo_id' });

      if (upsertError) throw upsertError;

      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Failed to toggle activation mode:', err);
    }
  }, [postgrest, fetchData]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t('screens.superAdmin.activation.overview.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('screens.superAdmin.activation.overview.description')}
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('screens.superAdmin.activation.overview.fboFilter')}</InputLabel>
          <Select
            value={selectedFboId}
            label={t('screens.superAdmin.activation.overview.fboFilter')}
            onChange={(e) => setSelectedFboId(e.target.value)}
          >
            <MenuItem value="">{t('screens.superAdmin.activation.overview.allFbos')}</MenuItem>
            {fbos.map((fbo) => (
              <MenuItem key={fbo.id} value={fbo.id}>{fbo.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder={t('screens.superAdmin.activation.overview.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('screens.superAdmin.activation.overview.filterLabel')}</InputLabel>
          <Select
            value={filter}
            label={t('screens.superAdmin.activation.overview.filterLabel')}
            onChange={(e) => setFilter(e.target.value as ActivationFilter)}
          >
            <MenuItem value="all">{t('screens.superAdmin.activation.overview.filters.all')}</MenuItem>
            <MenuItem value="active">{t('screens.superAdmin.activation.overview.filters.active')}</MenuItem>
            <MenuItem value="stalled">{t('screens.superAdmin.activation.overview.filters.stalled')}</MenuItem>
            <MenuItem value="deep">{t('screens.superAdmin.activation.overview.filters.deep')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.user')}</TableCell>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.fboName')}</TableCell>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.state')}</TableCell>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.autostackRuns')}</TableCell>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.checklist')}</TableCell>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.timeline')}</TableCell>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.mode')}</TableCell>
              <TableCell>{t('screens.superAdmin.activation.overview.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t('screens.superAdmin.activation.overview.noData')}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={`${row.user_id}-${row.fbo_id}`}
                  sx={{
                    bgcolor: row.is_stalled ? 'warning.50' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {row.user_name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.user_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.fbo_name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getStateLabel(row.activation_state, t)}
                      color={getStateChipColor(row.activation_state)}
                    />
                    {row.is_stalled && (
                      <Chip
                        size="small"
                        label="Stalled"
                        color="error"
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`Deep activation at ${row.deep_activation_threshold} runs`}>
                      <span>{row.autostack_run_count}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <ActivationProgress
                      completed={row.checklist_progress}
                      total={5}
                      variant="dots"
                      showLabel={false}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const info = getTimelineInfo(row);
                      if (!info) return <Typography variant="body2" color="text.secondary">-</Typography>;

                      const { daysRemaining, isExpired, startDate, expirationDate } = info;
                      const expirationLabel = isExpired
                        ? `Expired ${Math.abs(daysRemaining)}d ago`
                        : `${daysRemaining}d left`;

                      return (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption">Started: {startDate.toLocaleDateString()}</Typography>
                              <br />
                              <Typography variant="caption">Expires: {expirationDate.toLocaleDateString()}</Typography>
                              {row.last_activity_at && (
                                <>
                                  <br />
                                  <Typography variant="caption">Last active: {formatRelativeTime(row.last_activity_at)}</Typography>
                                </>
                              )}
                            </Box>
                          }
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography variant="body2">
                              {formatRelativeTime(row.activation_started_at)}
                            </Typography>
                            <Chip
                              size="small"
                              label={expirationLabel}
                              color={isExpired ? 'error' : daysRemaining <= 3 ? 'warning' : 'default'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 18, width: 'fit-content' }}
                            />
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={row.activation_mode}
                      onChange={() => handleToggleMode(row)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('screens.superAdmin.activation.overview.viewDetails')}>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedUser(row)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      {selectedUser && (
        <ActivationDetailDialog
          open={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          user={selectedUser}
          onUpdate={fetchData}
        />
      )}
    </Box>
  );
};
